import { Copy, Check, ThumbsUp, ThumbsDown } from "lucide-react";
import { useState, useRef, useEffect, useCallback } from "react";

// Display-only highlighting (e.g. red/green findings, unknown medicines) is
// done with <span> wrappers. Never show those tags to the user while editing.
const stripSpanTags = (value) =>
  typeof value === "string" ? value.replace(/<\/?span\b[^>]*>/gi, "") : value;

export const InlineEditableLine = ({
  text,
  copyText,
  isParagraph = false,
  formatAsParagraphs,
  onFeedback,
  section,
  feedbackSubmitted,
  onSave,
  onUpdateSoapNote,
  soapNoteId,
  paragraphIndex,
  readOnly = false,
  onUnknownMedicationClick,
}) => {
  const [copied, setCopied] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const contentRef = useRef(null);
  const [originalText, setOriginalText] = useState(text);
  const [userInput, setUserInput] = useState(text);

  // Keep originalText in sync with prop
  useEffect(() => {
    setOriginalText(text);
  }, [text]);

  // Copy handler
  const handleCopy = async () => {
    const sourceText = typeof copyText === "string" ? copyText : text;
    const plainText = sourceText
      .replace(/<[^>]*>/g, "")
      .replace(/\r\n/g, "\n")
      .replace(/[ \t]+\n/g, "\n")
      .trim()
      .replace(/^\s*([•-]\s?)/, "");

    await navigator.clipboard.writeText(plainText);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  // Feedback handler
  const handleFeedback = (type) => {
    if (onFeedback && !readOnly) {
      onFeedback(
        section,
        type,
        text,
        isParagraph ? "paragraph" : "bullet-point",
      );
    }
  };

  // Enter edit mode on click (if not read-only and not selecting text)
  const handleClick = (e) => {
    if (e.target?.closest?.(".practice-partner-mention")) return;
    if (e.target?.closest?.("[data-icd-code]")) return;
    const unknownMedicine = e.target?.closest?.("[data-unknown-med-id]");
    if (unknownMedicine) {
      e.preventDefault();
      e.stopPropagation();
      let originalText = unknownMedicine.getAttribute("data-unknown-med-text") || "";
      try {
        originalText = decodeURIComponent(originalText);
      } catch {
        // Keep the raw attribute when it is not encoded.
      }
      onUnknownMedicationClick?.({
        id: unknownMedicine.getAttribute("data-unknown-med-id"),
        originalText,
        localOccurrence: Number(unknownMedicine.getAttribute("data-unknown-med-index") || 0),
        blockText: text,
        section,
      });
      return;
    }
    if (readOnly) return;

    const selection = window.getSelection();
    if (selection && selection.toString().length > 0) return;

    // If there was a previous save error, clear it when re-entering edit
    setSaveError(null);
    const editableText = stripSpanTags(text);
    setIsEditing(true);
    setUserInput(editableText);

    // Set the content to plain text for editing
    setTimeout(() => {
      if (contentRef.current) {
        contentRef.current.textContent = editableText;

        // Try to place cursor at click position
        const rect = contentRef.current.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const clickY = e.clientY - rect.top;

        const range = document.createRange();
        const sel = window.getSelection();

        try {
          const textNodes = [];
          const walker = document.createTreeWalker(
            contentRef.current,
            NodeFilter.SHOW_TEXT,
            null,
            false,
          );

          let node;
          while ((node = walker.nextNode())) {
            textNodes.push(node);
          }

          if (textNodes.length === 0) {
            contentRef.current.focus();
            return;
          }

          let bestNode = textNodes[0];
          let bestOffset = 0;
          let bestDistance = Infinity;

          for (const textNode of textNodes) {
            const nodeText = textNode.textContent;
            const nodeLength = nodeText.length;

            for (let i = 0; i <= nodeLength; i++) {
              try {
                range.setStart(textNode, Math.min(i, nodeLength));
                range.setEnd(textNode, Math.min(i, nodeLength));
                range.collapse(true);

                const rects = range.getClientRects();
                if (rects.length > 0) {
                  const charRect = rects[0];
                  const distance =
                    Math.abs(clickX - (charRect.left - rect.left)) +
                    Math.abs(clickY - (charRect.top - rect.top));

                  if (distance < bestDistance) {
                    bestDistance = distance;
                    bestNode = textNode;
                    bestOffset = i;
                  }
                }
              } catch (err) {
                continue;
              }
            }
          }

          range.setStart(
            bestNode,
            Math.min(bestOffset, bestNode.textContent.length),
          );
          range.setEnd(
            bestNode,
            Math.min(bestOffset, bestNode.textContent.length),
          );
          range.collapse(true);
          sel.removeAllRanges();
          sel.addRange(range);
        } catch (err) {
          console.warn("Cursor positioning failed, using fallback:", err);
          range.selectNodeContents(contentRef.current);
          range.collapse(false);
          sel.removeAllRanges();
          sel.addRange(range);
        }
      }
    }, 0);
  };

  // Save changes and exit edit mode (or keep editing on error)
  const handleSave = useCallback(async () => {
    if (!isEditing) return;
    if (!contentRef.current) return;
    if (!onSave || readOnly) return;

    // Extract plain text from contentEditable (with newlines)
    const rawHtml = contentRef.current.innerHTML;
    const newText = rawHtml
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/p>/gi, "\n")
      .replace(/<p[^>]*>/gi, "")
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, " ")
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .trim();

    if (newText === stripSpanTags(originalText)) {
      // No change, just exit edit mode
      setIsEditing(false);
      setSaveError(null);
      return;
    }

    setIsSaving(true);
    setSaveError(null);

    try {
      // Call the parent's save handler (async)
      await onSave(section, newText);
      // Save succeeded: update originalText, exit edit mode
      setOriginalText(newText);
      setIsEditing(false);
      setSaveError(null);
    } catch (error) {
      console.error("Failed to save inline edit:", error);
      setSaveError(error.message || "Failed to save changes");
      // Keep editing mode and show error indicator
      // The parent may already show a toast; we keep the text available.
    } finally {
      setIsSaving(false);
    }
  }, [isEditing, onSave, readOnly, section, originalText, contentRef]);

  // Blur handler: attempt save
  const handleBlur = useCallback(
    async (e) => {
      if (!isEditing) return;
      if (isSaving) return;

      // If the blur is triggered by clicking on a feedback/copy button inside the hover menu, we should not save.
      // We can check if the related target is inside the wrapper.
      const relatedTarget = e.relatedTarget;
      if (relatedTarget && relatedTarget.closest?.(".inline-actions")) {
        return;
      }

      await handleSave();
    },
    [isEditing, isSaving, handleSave],
  );

  // Keyboard shortcuts: Enter (new line), Ctrl+Enter (save), Escape (cancel)
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        handleSave();
      } else if (e.key === "Escape") {
        e.preventDefault();
        // Cancel: revert to original text and exit edit mode
        if (contentRef.current) {
          contentRef.current.textContent = stripSpanTags(originalText);
        }
        setIsEditing(false);
        setSaveError(null);
      } else if (e.key === "Enter") {
        // For contentEditable, allow default Enter to create new line.
        // We do not prevent default; contentEditable will insert a <br> or <div>.
        // If we want consistent newline behavior, we can let the browser handle it.
      }
    },
    [handleSave, originalText],
  );

  // When editing starts, focus and place cursor at end (for safety)
  useEffect(() => {
    if (isEditing && contentRef.current) {
      contentRef.current.focus();
      const range = document.createRange();
      const sel = window.getSelection();
      range.selectNodeContents(contentRef.current);
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
    }
  }, [isEditing]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // No debounce to clean up
    };
  }, []);

  // Determine wrapper element based on isParagraph
  const Wrapper = isParagraph ? "div" : "li";

  // Determine if we should show the inline action buttons (copy, like, dislike)
  const showActions = isHovered && !isEditing && !readOnly;

  // Determine the display content when not editing
  const displayContent = !isEditing ? formatAsParagraphs(text) : undefined;

  // Styles for editing mode: keep it visually identical to non-editing
  const editingStyles =
    isEditing && !readOnly
      ? {
          whiteSpace: "pre-wrap",
          wordWrap: "break-word",
          lineHeight: "1.5",
          outline: "none",
          boxShadow: "none",
          // Remove any padding/border that might be added by className
        }
      : {};

  return (
    <Wrapper
      className="relative group pl-2 pr-8 rounded-md hover:bg-gray-50 transition-colors"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        ref={contentRef}
        className={`prose prose-sm max-w-none text-gray-700 ${
          isEditing && !readOnly
            ? "cursor-text focus:outline-none focus:ring-0"
            : readOnly
              ? "cursor-default"
              : "cursor-pointer"
        }`}
        contentEditable={isEditing && !readOnly}
        suppressContentEditableWarning={true}
        onClick={!isEditing ? handleClick : undefined}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        title={
          readOnly
            ? "Read-only (approved)"
            : isEditing
              ? "Editing... (Ctrl+Enter to save, Escape to cancel)"
              : "Click to edit"
        }
        dangerouslySetInnerHTML={
          displayContent ? { __html: displayContent } : undefined
        }
        style={editingStyles}
      />

      {/* Saving indicator (spinner) */}
      {isSaving && (
        <div
          className="absolute top-1 right-1 w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"
          title="Saving..."
        />
      )}

      {/* Error indicator */}
      {saveError && (
        <div
          className="absolute top-1 right-6 text-red-500 text-xs font-medium"
          title={saveError}
        >
          ⚠️
        </div>
      )}

      {/* Action buttons (only when not editing and not read-only) */}
      {showActions && (
        <div className="absolute top-1/2 right-2 -translate-y-1/2 flex items-center gap-1 bg-white rounded-full shadow-sm border p-1 inline-actions">
          <button
            onClick={handleCopy}
            className="p-1 rounded-full hover:bg-gray-100 transition-colors"
            title="Copy"
          >
            {copied ? (
              <Check className="w-4 h-4 text-green-600" />
            ) : (
              <Copy className="w-4 h-4 text-gray-600" />
            )}
          </button>

          <button
            onClick={() => handleFeedback("like")}
            disabled={feedbackSubmitted !== null || readOnly}
            className={`p-1 rounded-full transition-colors ${
              feedbackSubmitted === "like"
                ? "bg-green-100 text-green-600 cursor-default"
                : feedbackSubmitted !== null || readOnly
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "hover:bg-green-50 text-gray-600"
            }`}
            title={
              readOnly
                ? "SOAP note is approved and read-only"
                : feedbackSubmitted === "like"
                  ? "Already liked"
                  : feedbackSubmitted !== null
                    ? "Feedback already submitted"
                    : "Like"
            }
          >
            {feedbackSubmitted === "like" ? (
              <ThumbsUp fill="#17803C" className="w-4 h-4" />
            ) : feedbackSubmitted !== null || readOnly ? (
              <ThumbsUp className="w-4 h-4 text-gray-400" />
            ) : (
              <ThumbsUp className="w-4 h-4" />
            )}
          </button>

          <button
            onClick={() => handleFeedback("dislike")}
            disabled={feedbackSubmitted !== null || readOnly}
            className={`p-1 rounded-full transition-colors ${
              feedbackSubmitted === "dislike"
                ? "bg-red-100 text-red-600 cursor-default"
                : feedbackSubmitted !== null || readOnly
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "hover:bg-red-50 text-gray-600"
            }`}
            title={
              readOnly
                ? "SOAP note is approved and read-only"
                : feedbackSubmitted === "dislike"
                  ? "Already disliked"
                  : feedbackSubmitted !== null
                    ? "Feedback already submitted"
                    : "Dislike"
            }
          >
            {feedbackSubmitted === "dislike" ? (
              <ThumbsDown fill="#dc2626" className="w-4 h-4" />
            ) : feedbackSubmitted !== null || readOnly ? (
              <ThumbsDown className="w-4 h-4 text-gray-400" />
            ) : (
              <ThumbsDown className="w-4 h-4" />
            )}
          </button>
        </div>
      )}
    </Wrapper>
  );
};
